import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from '../lib/teact/teact'

import type { Scheduler } from '../util/schedulers'

import { debounce, throttle, throttleWith } from '../util/schedulers'
import useEffectOnce from './useEffectOnce'
import useHeavyAnimationCheck from './useHeavyAnimationCheck'

type TargetCallback = (entry: IntersectionObserverEntry) => void
type RootCallback = (entries: IntersectionObserverEntry[]) => void
type ObserveCleanup = NoneToVoidFunction
export type ObserveFn = (target: HTMLElement, targetCallback?: TargetCallback) => ObserveCleanup

interface IntersectionController {
  observer: IntersectionObserver
  callbacks: Map<HTMLElement, TargetCallback>
}

interface Response {
  observe: ObserveFn
  freeze: NoneToVoidFunction
  unfreeze: NoneToVoidFunction
}

export function useIntersectionObserver(
  {
    rootRef,
    throttleMs,
    throttleScheduler,
    debounceMs,
    shouldSkipFirst,
    margin,
    threshold,
    isDisabled,
  }: {
    rootRef: RefObject<HTMLDivElement | null>
    throttleMs?: number
    throttleScheduler?: Scheduler
    debounceMs?: number
    shouldSkipFirst?: boolean
    margin?: number
    threshold?: number | number[]
    isDisabled?: boolean
  },
  rootCallback?: RootCallback,
): Response {
  const controllerRef = useRef<IntersectionController>(undefined)
  const rootCallbackRef = useRef<RootCallback>(undefined)
  const freezeFlagsRef = useRef(0)
  const onUnfreezeRef = useRef<NoneToVoidFunction>(undefined)

  rootCallbackRef.current = rootCallback

  const freeze = useCallback(() => {
    freezeFlagsRef.current++
  }, [])

  const unfreeze = useCallback(() => {
    if (!freezeFlagsRef.current) {
      return
    }

    freezeFlagsRef.current--

    if (!freezeFlagsRef.current && onUnfreezeRef.current) {
      onUnfreezeRef.current()
      onUnfreezeRef.current = undefined
    }
  }, [])

  useHeavyAnimationCheck(freeze, unfreeze)

  useEffect(() => {
    if (isDisabled) {
      return undefined
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.observer.disconnect()
        controllerRef.current.callbacks.clear()
        controllerRef.current = undefined
      }
    }
  }, [isDisabled])

  function initController() {
    const callbacks = new Map()
    const entriesAccumulator = new Map<Element, IntersectionObserverEntry>()

    let observerCallback: typeof observerCallbackSync
    if (typeof throttleScheduler === 'function') {
      observerCallback = throttleWith(throttleScheduler, observerCallbackSync)
    } else if (throttleMs) {
      observerCallback = throttle(observerCallbackSync, throttleMs, !shouldSkipFirst)
    } else if (debounceMs) {
      observerCallback = debounce(observerCallbackSync, debounceMs, !shouldSkipFirst)
    } else {
      observerCallback = observerCallbackSync
    }

    function observerCallbackSync() {
      if (freezeFlagsRef.current) {
        onUnfreezeRef.current = observerCallback
        return
      }

      const entries = Array.from(entriesAccumulator.values())

      entries.forEach((entry: IntersectionObserverEntry) => {
        const callback = callbacks.get(entry.target)
        if (callback) {
          callback!(entry, entries)
        }
      })

      if (rootCallbackRef.current) {
        rootCallbackRef.current(entries)
      }

      entriesAccumulator.clear()
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entriesAccumulator.set(entry.target, entry)
        })

        if (freezeFlagsRef.current) {
          onUnfreezeRef.current = observerCallback
        } else {
          observerCallback()
        }
      },
      {
        root: rootRef.current,
        rootMargin: margin ? `${margin}px` : undefined,
        threshold,
      },
    )

    controllerRef.current = { observer, callbacks }
  }

  const observe = useCallback(
    (target: HTMLElement, targetCallback?: TargetCallback) => {
      if (!controllerRef.current) {
        initController()
      }

      const controller = controllerRef.current!
      controller.observer.observe(target)

      if (targetCallback) {
        controller.callbacks.set(target, targetCallback)
      }

      return () => {
        if (targetCallback) {
          controller.callbacks.delete(target)
        }

        controller.observer.unobserve(target)
      }
    },
    // Arguments should never change
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    [isDisabled],
  )

  return { observe, freeze, unfreeze }
}

export function useOnIntersect(
  targetRef: RefObject<HTMLDivElement | null>,
  observe?: ObserveFn,
  callback?: TargetCallback,
) {
  useEffectOnce(() => {
    return observe ? observe(targetRef.current!, callback) : undefined
    // Arguments should never change
  })
}

export function useIsIntersecting(
  targetRef: RefObject<HTMLDivElement | null>,
  observe?: ObserveFn,
  callback?: TargetCallback,
) {
  const [isIntersecting, setIsIntersecting] = useState(!observe)

  useOnIntersect(targetRef, observe, (entry) => {
    setIsIntersecting(entry.isIntersecting)

    if (callback) {
      callback(entry)
    }
  })

  return isIntersecting
}
